import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, MessageSquare, AlertTriangle, CheckCircle2,
  XCircle, Loader2, Users, Inbox, Sparkles, History, RotateCcw,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import InlineConfirm from "../../components/admin/InlineConfirm";

/**
 * Brief 3.4.8 — Onboarding session detail.
 *
 * Drill-in for a single `telegram_group_onboarding_sessions` row.
 * Shows the conversation transcript, the accumulated extraction state,
 * duplicate candidates (when in duplicate_pending_review), and four
 * admin action CTAs:
 *   • Confirm new merchant (from duplicate_pending_review only)
 *   • Mark duplicate
 *   • Abort
 *   • Resume (from provisioning_failed_* only)
 */
export default function AdminOnboardingSessionDetail() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionInflight, setActionInflight] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/onboarding-sessions/${sessionId}`);
      setSession(data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load session.");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { refresh(); }, [refresh]);

  if (user?.role !== "super_admin") {
    return <div data-testid="onboarding-detail-forbidden" className="text-rose-200">Super-admin only.</div>;
  }

  const fire = async (path, body, label) => {
    if (actionInflight) return;
    setActionInflight(path);
    try {
      await api.post(`/admin/onboarding-sessions/${sessionId}/${path}`, body || {});
      toast.success(`${label} done.`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed.`);
    } finally {
      setActionInflight(null);
    }
  };

  return (
    <div data-testid="onboarding-detail-page" className="max-w-5xl space-y-6 pb-16">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/admin/onboarding-sessions" className="inline-flex items-center gap-1.5 text-[11.5px] text-zinc-400 hover:text-white" data-testid="onboarding-detail-back">
          <ArrowLeft className="h-3 w-3" /> Back to sessions
        </Link>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 text-[11.5px] text-zinc-400 hover:text-white"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {error && <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.03] p-4 text-rose-200 text-[12.5px]">{error}</div>}

      {session && (
        <div className="space-y-5">
          <HeaderCard session={session} />
          <ExtractionCard state={session.extracted_state} />
          <DuplicateCard session={session} onConfirmNew={() => fire("confirm-new-merchant", {}, "Confirm new merchant")} onMarkDup={(mid) => fire("mark-duplicate", {duplicate_of_merchant_id: mid}, "Mark duplicate")} actionInflight={actionInflight} />
          <TranscriptCard messages={session.messages || []} />
          <ActionsCard session={session} actionInflight={actionInflight} fire={fire} />
        </div>
      )}
    </div>
  );
}

function HeaderCard({ session }) {
  return (
    <section data-testid="onboarding-detail-header" className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Inbox className="h-4 w-4 text-cyan-300/80" />
          <h2 className="text-[15px] text-white">Session {session.session_id}</h2>
        </div>
        <span className="rounded-full border border-white/[0.10] bg-white/[0.04] px-2.5 py-0.5 text-[11px] text-zinc-300">
          {session.status}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px]">
        <KeyValue k="Chat ID" v={<span className="font-mono">{session.chat_id}</span>} />
        <KeyValue k="Group title" v={session.group_title || "—"} />
        <KeyValue k="Joined at" v={fmt(session.joined_at)} />
        <KeyValue k="Intro sent at" v={fmt(session.intro_sent_at)} />
        <KeyValue k="Confirmed at" v={fmt(session.confirmed_at)} />
        <KeyValue k="Completed at" v={fmt(session.completed_at)} />
        {session.merchant_id && (
          <KeyValue k="Merchant" v={
            <Link to={`/admin/merchants/${session.merchant_id}`} className="text-cyan-300/80 hover:text-cyan-200">
              {session.merchant_id}
            </Link>
          } />
        )}
      </div>
    </section>
  );
}

function ExtractionCard({ state }) {
  if (!state) return null;
  const ln = state.legal_name || {};
  const pc = state.primary_contact || {};
  return (
    <section data-testid="onboarding-detail-extraction" className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
      <header className="flex items-center gap-2.5 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-zinc-400" />
        <h3 className="text-[13px] text-zinc-200">Extracted state</h3>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px]">
        <KeyValue k="Legal name" v={
          <span>
            {ln.value || <span className="text-zinc-600">—</span>}
            {ln.confidence !== undefined && (
              <span className="ml-2 text-[10.5px] text-zinc-500">conf {ln.confidence?.toFixed(2)}</span>
            )}
            {ln.ambiguous && <span className="ml-2 text-[10.5px] text-amber-200">ambiguous</span>}
          </span>
        } />
        <KeyValue k="Primary contact" v={
          <span>
            {pc.handle ? `@${pc.handle}` : (pc.name || <span className="text-zinc-600">—</span>)}
            {pc.confidence !== undefined && (
              <span className="ml-2 text-[10.5px] text-zinc-500">conf {pc.confidence?.toFixed(2)}</span>
            )}
          </span>
        } />
      </div>
      <div className="mt-3">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Members</div>
        <ul className="text-[12.5px] text-zinc-200 space-y-0.5">
          {(state.members || []).length === 0 && (
            <li className="text-zinc-600 italic text-[11.5px]">— none captured —</li>
          )}
          {(state.members || []).map((m, i) => (
            <li key={i}>
              {m.handle ? `@${m.handle}` : ""}
              {m.handle && m.name ? " · " : ""}
              {m.name || ""}
            </li>
          ))}
        </ul>
      </div>
      {state.still_missing && state.still_missing.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[10.5px] text-zinc-500">still missing:</span>
          {state.still_missing.map((s) => (
            <span key={s} className="rounded-full border border-amber-400/25 bg-amber-400/[0.04] px-2 py-0.5 text-[10.5px] text-amber-200 font-mono">{s}</span>
          ))}
        </div>
      )}
    </section>
  );
}

function DuplicateCard({ session, onConfirmNew, onMarkDup, actionInflight }) {
  if (session.status !== "duplicate_pending_review") return null;
  const candidates = session.duplicate_candidates || [];
  return (
    <section data-testid="onboarding-detail-duplicates" className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.04] p-5">
      <header className="flex items-center gap-2.5 mb-3">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-300" />
        <h3 className="text-[13px] text-amber-100">Duplicate review pending</h3>
      </header>
      <p className="text-[12px] text-amber-200/80 mb-3">
        The legal name confirmed in this session matches {candidates.length} existing merchant(s).
        Confirm not-a-duplicate to proceed with provisioning, or mark which existing merchant
        this is.
      </p>
      <ul className="space-y-2 text-[12.5px]">
        {candidates.map((c) => (
          <li key={c.merchant_id} data-testid={`onboarding-detail-dup-${c.merchant_id}`} className="rounded-md border border-white/[0.10] bg-black/20 p-3 flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-zinc-100">{c.name}</div>
              <div className="text-[11px] text-zinc-500 font-mono">
                {c.ref} · {c.merchant_id} · {c.status} · {c.match_reason}
              </div>
            </div>
            <InlineConfirm
              message="Mark this as the duplicate?"
              onConfirm={() => onMarkDup(c.merchant_id)}
              testid={`onboarding-detail-mark-dup-${c.merchant_id}`}
              tone="danger"
              trigger={({ onClick }) => (
                <button
                  type="button"
                  onClick={onClick}
                  disabled={!!actionInflight}
                  data-testid={`onboarding-detail-mark-dup-${c.merchant_id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-rose-400/30 bg-rose-400/[0.06] px-2.5 py-1 text-[11px] text-rose-100 hover:bg-rose-400/[0.10] disabled:opacity-40"
                >
                  {actionInflight === "mark-duplicate" ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                  Mark this as the duplicate
                </button>
              )}
            />
          </li>
        ))}
      </ul>
      <div className="mt-3">
        <InlineConfirm
          message="Confirm not a duplicate? Proceeds to provisioning."
          onConfirm={onConfirmNew}
          testid="onboarding-detail-confirm-new"
          trigger={({ onClick }) => (
            <button
              type="button"
              onClick={onClick}
              disabled={!!actionInflight}
              data-testid="onboarding-detail-confirm-new"
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-400/30 bg-emerald-400/[0.06] px-3 py-1.5 text-[12px] text-emerald-100 hover:bg-emerald-400/[0.10] disabled:opacity-40"
            >
              {actionInflight === "confirm-new-merchant" ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Confirm not a duplicate · proceed with provisioning
            </button>
          )}
        />
      </div>
    </section>
  );
}

function TranscriptCard({ messages }) {
  return (
    <section data-testid="onboarding-detail-transcript" className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
      <header className="flex items-center gap-2.5 mb-3">
        <MessageSquare className="h-3.5 w-3.5 text-zinc-400" />
        <h3 className="text-[13px] text-zinc-200">Transcript ({messages.length})</h3>
      </header>
      {messages.length === 0 ? (
        <div className="text-[12px] text-zinc-500 italic">No messages yet.</div>
      ) : (
        <ul className="space-y-2 text-[12.5px]">
          {messages.map((m, i) => (
            <li key={i} className="rounded-md border-l-2 border-white/[0.10] pl-3 py-1">
              <div className="text-[11px] text-zinc-500">
                <span className="text-zinc-300">{m.sender_username ? `@${m.sender_username}` : m.sender_user_id}</span>
                <span className="text-zinc-600"> · {fmt(m.at)}</span>
              </div>
              <div className="text-zinc-100 whitespace-pre-wrap break-words">{m.text}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ActionsCard({ session, actionInflight, fire }) {
  const isFailed = (session.status || "").startsWith("provisioning_failed_");
  const isTerminal = ["done", "marked_duplicate", "aborted",
                      "awaiting_handle_grace_expired"].includes(session.status);
  if (isTerminal) {
    return (
      <section data-testid="onboarding-detail-actions" className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 text-[12px] text-zinc-500 italic">
        Session is terminal — no further actions available.
      </section>
    );
  }
  return (
    <section data-testid="onboarding-detail-actions" className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5">
      <header className="flex items-center gap-2.5 mb-3">
        <History className="h-3.5 w-3.5 text-zinc-400" />
        <h3 className="text-[13px] text-zinc-200">Admin actions</h3>
      </header>
      <div className="flex flex-wrap items-center gap-2">
        {isFailed && (
          <InlineConfirm
            message="Resume provisioning?"
            onConfirm={() => fire("resume", {}, "Resume provisioning")}
            testid="onboarding-detail-resume"
            trigger={({ onClick }) => (
              <button
                type="button"
                onClick={onClick}
                disabled={!!actionInflight}
                data-testid="onboarding-detail-resume"
                className="inline-flex items-center gap-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/[0.08] px-3 py-1.5 text-[11.5px] text-cyan-100 hover:bg-cyan-400/[0.14] disabled:opacity-40"
              >
                {actionInflight === "resume" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                Resume
              </button>
            )}
          />
        )}
        <InlineConfirm
          message="Abort this session?"
          onConfirm={() => fire("abort", {reason: "manual"}, "Abort session")}
          testid="onboarding-detail-abort"
          tone="danger"
          trigger={({ onClick }) => (
            <button
              type="button"
              onClick={onClick}
              disabled={!!actionInflight}
              data-testid="onboarding-detail-abort"
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-400/30 bg-rose-400/[0.06] px-3 py-1.5 text-[11.5px] text-rose-100 hover:bg-rose-400/[0.10] disabled:opacity-40"
            >
              {actionInflight === "abort" ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
              Abort
            </button>
          )}
        />
      </div>
    </section>
  );
}

function KeyValue({ k, v }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">{k}</div>
      <div className="mt-0.5 text-zinc-200">{v ?? <span className="text-zinc-600">—</span>}</div>
    </div>
  );
}

function fmt(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(v);
  }
}
